import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Routes, Route } from 'react-router-dom';

// ===== DATA =====
const TECH_CATEGORIES = [
  { id: 'all', label: '全部技术', icon: 'ri-apps-line', color: '#00FFCC' },
  { id: 'meta', label: '元宇宙', icon: 'ri-earth-line', color: '#00FFCC' },
  { id: '6g', label: '6G超连接', icon: 'ri-signal-tower-line', color: '#FF6B35' },
  { id: 'ai', label: 'AI智能', icon: 'ri-brain-line', color: '#FFD700' },
  { id: 'quantum', label: '量子计算', icon: 'ri-focus-3-line', color: '#00FF88' },
  { id: 'xr', label: 'XR体验', icon: 'ri-glasses-2-line', color: '#FF6B9D' },
  { id: 'blockchain', label: '区块链', icon: 'ri-links-line', color: '#A855F7' },
];

const TECH_ITEMS = [
  {
    id: 1, category: 'meta',
    title: '数字孪生引擎 v5.0',
    subtitle: 'Digital Twin Engine',
    desc: '将物理世界1:1映射到元宇宙空间，实现实时同步、预测性维护和虚实协同操作。支持城市级别的数字孪生构建，精度达到毫米级。',
    image: 'https://public.readdy.ai/ai/img_res/8681c7343543ab47a2de46dfe96c4f22.jpg',
    tags: ['实时同步', '城市级别', '毫米精度'],
    progress: 92,
    status: '量产阶段',
    color: '#00FFCC',
    specs: [
      { label: '同步延迟', value: '<1ms' },
      { label: '精度', value: '0.1mm' },
      { label: '覆盖范围', value: '城市级' },
      { label: '数据处理', value: '10PB/s' },
    ],
    deepContent: {
      overview: '数字孪生引擎v5.0是META66核心技术体系的基石，通过量子传感器网络与AI实时建模，将物理世界的每一个细节以毫米级精度映射到虚拟空间。这不仅仅是3D建模，而是一个具有完整物理规律、实时数据流和预测能力的"平行世界"。',
      applications: [
        { title: '智慧城市管理', desc: '城市基础设施的实时监控与预测性维护，减少故障率90%', icon: 'ri-building-4-line' },
        { title: '工业制造优化', desc: '工厂生产线的数字孪生，实现零停机生产，效率提升300%', icon: 'ri-settings-3-line' },
        { title: '医疗手术模拟', desc: '患者器官的精准数字孪生，手术成功率提升至99.7%', icon: 'ri-heart-pulse-line' },
        { title: '气候变化预测', desc: '地球级数字孪生，提前30年预测气候变化趋势', icon: 'ri-earth-line' },
      ],
      timeline: [
        { year: '2024', event: '数字孪生引擎v4.0发布，支持区级别城市建模' },
        { year: '2025', event: 'v5.0发布，精度提升至毫米级，支持城市级别' },
        { year: '2027', event: '全球首个国家级数字孪生系统在新加坡上线' },
        { year: '2030', event: '地球数字孪生计划启动，全球协同建模' },
      ],
      techStack: ['量子传感器网络', 'AI实时建模引擎', '分布式计算集群', '光子通信协议', '物理仿真引擎'],
    },
  },
  {
    id: 2, category: '6g',
    title: '6G太赫兹通信',
    subtitle: 'THz Communication',
    desc: '突破5G极限，实现1Tbps超高速传输，端到端延迟低于0.1ms。支持全息通信、脑机接口实时传输和空间计算数据流。',
    image: 'https://readdy.ai/api/search-image?query=6G%20terahertz%20wireless%20communication%20network%20glowing%20orange%20neon%20signal%20waves%20dark%20background%20technology%20futuristic%20ultra%20detailed%20cinematic%208k&width=800&height=500&seq=tech-6g-v3&orientation=landscape',
    tags: ['1Tbps', '0.1ms延迟', '全息通信'],
    progress: 78,
    status: '测试阶段',
    color: '#FF6B35',
    specs: [
      { label: '峰值速率', value: '1Tbps' },
      { label: '延迟', value: '0.1ms' },
      { label: '频段', value: '0.1-10THz' },
      { label: '覆盖密度', value: '100万设备/km²' },
    ],
    deepContent: {
      overview: '6G太赫兹通信是元宇宙基础设施的神经系统。太赫兹频段（0.1-10THz）拥有比5G毫米波高出100倍的带宽，可实现1Tbps的峰值传输速率。这意味着一部8K全息电影可在0.01秒内传输完毕，脑机接口的神经信号可实现毫秒级实时传输。',
      applications: [
        { title: '全息通信', desc: '实时传输全息影像，实现"面对面"远程会议体验', icon: 'ri-user-3-line' },
        { title: '脑机接口传输', desc: '神经信号的超低延迟传输，支持意念控制元宇宙', icon: 'ri-mental-health-line' },
        { title: '空间计算数据流', desc: '支持XR设备的实时渲染数据流，告别本地计算限制', icon: 'ri-cpu-line' },
        { title: '工业物联网', desc: '100万设备/km²的超密集连接，支持智能工厂全面互联', icon: 'ri-settings-3-line' },
      ],
      timeline: [
        { year: '2025', event: '6G太赫兹通信首次商业测试，峰值速率达500Gbps' },
        { year: '2027', event: '6G标准冻结，全球统一协议确立' },
        { year: '2029', event: '6G商业部署启动，覆盖全球主要城市' },
        { year: '2032', event: '6G全球普及，元宇宙基础设施全面就绪' },
      ],
      techStack: ['太赫兹收发器', '智能超表面（RIS）', 'AI波束管理', '量子密钥分发', '卫星-地面一体化'],
    },
  },
  {
    id: 3, category: 'ai',
    title: '量子神经网络AI',
    subtitle: 'Quantum Neural AI',
    desc: '融合量子计算与深度学习，实现指数级计算加速。在药物研发、气候预测、金融建模等领域展现出超越人类的分析能力。',
    image: 'https://readdy.ai/api/search-image?query=quantum%20neural%20network%20artificial%20intelligence%20brain%20visualization%20glowing%20golden%20yellow%20neon%20connections%20dark%20background%20futuristic%20ultra%20detailed%20cinematic%208k&width=800&height=500&seq=tech-qai-v3&orientation=landscape',
    tags: ['量子加速', '指数级算力', '多领域应用'],
    progress: 65,
    status: '研发阶段',
    color: '#FFD700',
    specs: [
      { label: '量子比特', value: '10000+' },
      { label: '计算加速', value: '10^6倍' },
      { label: '准确率', value: '99.9%' },
      { label: '能耗', value: '降低99%' },
    ],
    deepContent: {
      overview: '量子神经网络AI是人工智能发展史上最重大的范式转变。通过将量子叠加态与神经网络的并行计算相结合，实现了对经典计算机的指数级超越。一个10000量子比特的量子神经网络，其计算能力相当于10^3000台经典超级计算机同时运行。',
      applications: [
        { title: '新药研发', desc: '在数小时内完成传统需要数十年的分子模拟，加速药物发现', icon: 'ri-medicine-bottle-line' },
        { title: '气候建模', desc: '精确预测未来100年的气候变化，为碳中和提供科学依据', icon: 'ri-cloud-line' },
        { title: '金融风控', desc: '实时分析全球市场的所有变量，预测金融风险准确率99.9%', icon: 'ri-stock-line' },
        { title: '材料科学', desc: '设计具有特定量子性质的新材料，推动超导、储能技术突破', icon: 'ri-flask-line' },
      ],
      timeline: [
        { year: '2026', event: '量子神经网络突破10000量子比特，实现量子优越性' },
        { year: '2028', event: '量子AI在药物研发领域首次超越人类专家' },
        { year: '2030', event: '量子AI云服务商业化，向全球开发者开放' },
        { year: '2035', event: '量子AI与AGI融合，超级智能时代开启' },
      ],
      techStack: ['超导量子比特', '量子纠错码', '变分量子算法', '量子-经典混合架构', '低温控制系统'],
    },
  },
  {
    id: 4, category: 'quantum',
    title: '量子加密通信',
    subtitle: 'Quantum Cryptography',
    desc: '基于量子纠缠的绝对安全通信协议，任何窃听行为都会被立即检测。已在金融、军事、政务领域实现商业化部署。',
    image: 'https://readdy.ai/api/search-image?query=quantum%20cryptography%20encryption%20secure%20communication%20glowing%20green%20neon%20particles%20entanglement%20dark%20background%20technology%20ultra%20detailed%20cinematic%208k&width=800&height=500&seq=tech-qc-v3&orientation=landscape',
    tags: ['绝对安全', '量子纠缠', '商业化'],
    progress: 88,
    status: '商业化',
    color: '#00FF88',
    specs: [
      { label: '密钥速率', value: '10Mbps' },
      { label: '传输距离', value: '1000km' },
      { label: '安全等级', value: '量子级' },
      { label: '部署节点', value: '500+城市' },
    ],
    deepContent: {
      overview: '量子加密通信基于量子力学的基本原理——海森堡不确定性原理和量子不可克隆定理，从物理层面保证通信的绝对安全。任何试图窃听的行为都会不可避免地改变量子态，从而被立即检测。这是人类历史上第一种在数学上可以证明绝对安全的通信方式。',
      applications: [
        { title: '金融交易安全', desc: '银行间结算、数字货币交易的量子级安全保障', icon: 'ri-bank-line' },
        { title: '政务通信', desc: '国家级机密通信的量子加密保护，防止国家级黑客攻击', icon: 'ri-government-line' },
        { title: '医疗数据保护', desc: '患者隐私数据的量子加密存储与传输', icon: 'ri-hospital-line' },
        { title: '元宇宙身份认证', desc: '数字身份的量子级安全认证，防止身份盗用', icon: 'ri-fingerprint-line' },
      ],
      timeline: [
        { year: '2024', event: '量子密钥分发（QKD）网络覆盖中国主要城市' },
        { year: '2026', event: '量子卫星通信实现全球覆盖，传输距离突破1000km' },
        { year: '2028', event: '量子互联网骨干网建成，连接全球主要数据中心' },
        { year: '2030', event: '量子加密成为互联网标准协议，全面替代传统加密' },
      ],
      techStack: ['量子密钥分发（QKD）', '量子纠缠光源', '单光子探测器', '量子中继器', '后量子密码学'],
    },
  },
  {
    id: 5, category: 'xr',
    title: '全息空间计算',
    subtitle: 'Holographic Spatial Computing',
    desc: '突破屏幕限制，将数字内容投影到真实空间中。支持多人协作、远程临场和沉浸式教育，开创人机交互新范式。',
    image: 'https://readdy.ai/api/search-image?query=holographic%20spatial%20computing%20augmented%20reality%20projection%20hands%20touching%20hologram%20futuristic%20interface%20glowing%20pink%20neon%20dark%20background%20ultra%20detailed%20cinematic%208k&width=800&height=500&seq=tech-holo-v3&orientation=landscape',
    tags: ['无屏幕', '多人协作', '远程临场'],
    progress: 71,
    status: '测试阶段',
    color: '#FF6B9D',
    specs: [
      { label: '分辨率', value: '16K/眼' },
      { label: '视场角', value: '220°' },
      { label: '刷新率', value: '240Hz' },
      { label: '重量', value: '<50g' },
    ],
    deepContent: {
      overview: '全息空间计算代表着人机交互的终极形态——彻底摆脱屏幕的束缚，将数字世界直接叠加在物理现实之上。通过光场显示技术和空间感知算法，数字内容可以精确地"放置"在真实空间的任意位置，与物理环境完美融合，让人无法区分虚实。',
      applications: [
        { title: '沉浸式教育', desc: '学生可以"走进"历史事件、"触摸"分子结构，学习效率提升500%', icon: 'ri-graduation-cap-line' },
        { title: '远程协作', desc: '全息会议让远程同事"出现"在同一空间，协作效率媲美面对面', icon: 'ri-team-line' },
        { title: '医疗可视化', desc: '外科医生可以看到患者体内的3D全息影像，精准手术', icon: 'ri-heart-pulse-line' },
        { title: '工业设计', desc: '设计师可以在真实空间中操作1:1比例的全息产品模型', icon: 'ri-pencil-ruler-line' },
      ],
      timeline: [
        { year: '2025', event: '全息眼镜重量突破50g，视场角达到120°' },
        { year: '2027', event: '全息空间计算设备消费级发布，价格降至5000元以内' },
        { year: '2029', event: '全息互联网标准确立，内容生态爆发' },
        { year: '2032', event: '全息空间计算取代智能手机，成为主流计算平台' },
      ],
      techStack: ['光场显示技术', '空间感知SLAM', '手势识别AI', '光子芯片', '眼动追踪'],
    },
  },
  {
    id: 6, category: 'blockchain',
    title: 'Web5.0去中心化协议',
    subtitle: 'Web5.0 Protocol',
    desc: '融合区块链、AI和量子加密的下一代互联网协议。用户完全掌控自己的数据和数字身份，实现真正的数字主权。',
    image: 'https://readdy.ai/api/search-image?query=web5%20decentralized%20blockchain%20network%20nodes%20glowing%20purple%20neon%20connections%20digital%20sovereignty%20dark%20background%20technology%20ultra%20detailed%20cinematic%208k&width=800&height=500&seq=tech-web5-v3&orientation=landscape',
    tags: ['数字主权', '去中心化', '量子安全'],
    progress: 55,
    status: '研发阶段',
    color: '#A855F7',
    specs: [
      { label: '交易速度', value: '100万TPS' },
      { label: '能耗', value: '降低99.9%' },
      { label: '节点数', value: '10亿+' },
      { label: '兼容性', value: '全链互通' },
    ],
    deepContent: {
      overview: 'Web5.0是互联网发展的终极形态，它将Web3.0的去中心化理念与AI的智能化能力和量子加密的绝对安全性完美融合。在Web5.0世界中，用户的数据、身份、资产完全由自己掌控，平台无法审查、无法没收、无法监控。这是真正意义上的"数字主权"时代。',
      applications: [
        { title: '数字身份主权', desc: '用户拥有唯一的去中心化数字身份，跨平台无缝使用', icon: 'ri-fingerprint-line' },
        { title: '数据资产化', desc: '个人数据成为可交易的数字资产，用户从数据中获益', icon: 'ri-database-line' },
        { title: '去中心化金融', desc: '无需银行的金融服务，全球任何人都可以访问', icon: 'ri-money-cny-circle-line' },
        { title: '元宇宙经济体', desc: '跨平台的数字资产互通，构建统一的元宇宙经济体系', icon: 'ri-store-3-line' },
      ],
      timeline: [
        { year: '2025', event: 'Web5.0白皮书发布，全球开发者社区形成' },
        { year: '2027', event: 'Web5.0测试网上线，100万开发者参与' },
        { year: '2029', event: 'Web5.0主网发布，首批去中心化应用上线' },
        { year: '2032', event: 'Web5.0成为互联网主流协议，数字主权时代开启' },
      ],
      techStack: ['量子安全区块链', 'AI智能合约', '去中心化存储', '零知识证明', '跨链互操作协议'],
    },
  },
  {
    id: 7, category: 'ai',
    title: '脑机接口 BCI 3.0',
    subtitle: 'Brain-Computer Interface',
    desc: '非侵入式神经信号读取技术，实现思维直接控制数字设备。支持意念打字、情绪感知和记忆增强，开启人机融合新纪元。',
    image: 'https://readdy.ai/api/search-image?query=brain%20computer%20interface%20neural%20signals%20visualization%20human%20brain%20glowing%20cyan%20connections%20technology%20futuristic%20dark%20background%20ultra%20detailed%20cinematic%208k&width=800&height=500&seq=tech-bci-v3&orientation=landscape',
    tags: ['非侵入式', '意念控制', '记忆增强'],
    progress: 48,
    status: '研发阶段',
    color: '#00FFCC',
    specs: [
      { label: '信号精度', value: '单神经元' },
      { label: '延迟', value: '<5ms' },
      { label: '带宽', value: '1Gbps' },
      { label: '续航', value: '72小时' },
    ],
    deepContent: {
      overview: '脑机接口BCI 3.0代表着人类与数字世界融合的最终形态。通过非侵入式神经信号采集技术，用户无需任何物理操作，仅凭意念即可控制元宇宙中的一切。这不仅仅是一种输入方式的革命，更是人类认知能力的根本性扩展——记忆可以被增强、技能可以被下载、情感可以被共享。',
      applications: [
        { title: '意念控制元宇宙', desc: '用思维直接操控虚拟世界，实现最自然的人机交互', icon: 'ri-earth-line' },
        { title: '医疗康复', desc: '帮助瘫痪患者重新控制肢体，恢复运动能力', icon: 'ri-wheelchair-line' },
        { title: '记忆增强', desc: '将外部信息直接写入记忆，学习速度提升1000倍', icon: 'ri-book-open-line' },
        { title: '情感通信', desc: '直接传递情感体验，超越语言的沟通方式', icon: 'ri-heart-line' },
      ],
      timeline: [
        { year: '2026', event: 'BCI 3.0非侵入式设备消费级原型发布' },
        { year: '2028', event: '意念输入延迟降至5ms，支持基础元宇宙操控' },
        { year: '2030', event: 'BCI与XR设备深度集成，全感官元宇宙实现' },
        { year: '2033', event: '记忆增强功能获批，人类认知能力全面提升' },
      ],
      techStack: ['高密度EEG阵列', 'AI神经解码', '实时信号处理', '无线神经传输', '神经反馈系统'],
    },
  },
  {
    id: 8, category: 'meta',
    title: '元宇宙物理引擎',
    subtitle: 'Metaverse Physics Engine',
    desc: '完全模拟真实物理定律的虚拟世界引擎，支持流体动力学、量子效应和相对论效应的实时计算，让虚拟世界与现实无缝融合。',
    image: 'https://readdy.ai/api/search-image?query=metaverse%20physics%20engine%20virtual%20world%20simulation%20particles%20fluid%20dynamics%20glowing%20orange%20neon%20dark%20background%20futuristic%20ultra%20detailed%20cinematic%208k&width=800&height=500&seq=tech-mpe-v3&orientation=landscape',
    tags: ['物理真实', '量子效应', '实时计算'],
    progress: 82,
    status: '量产阶段',
    color: '#FF6B35',
    specs: [
      { label: '物理精度', value: '量子级' },
      { label: '渲染帧率', value: '1000fps' },
      { label: '场景规模', value: '星球级' },
      { label: '用户并发', value: '10亿+' },
    ],
    deepContent: {
      overview: '元宇宙物理引擎是构建"真实感"虚拟世界的核心技术。它不仅仅是游戏引擎的升级版，而是一个能够精确模拟从量子尺度到宇宙尺度所有物理现象的超级计算系统。在这个引擎驱动的元宇宙中，水的流动、光的折射、材料的形变都与现实世界完全一致，让人无法区分虚实。',
      applications: [
        { title: '虚拟世界构建', desc: '构建具有完整物理规律的虚拟宇宙，支持10亿用户同时在线', icon: 'ri-earth-line' },
        { title: '科学实验模拟', desc: '在虚拟空间中进行无法在现实中完成的极端物理实验', icon: 'ri-flask-line' },
        { title: '建筑工程仿真', desc: '在建造前精确模拟建筑物在各种极端条件下的表现', icon: 'ri-building-line' },
        { title: '气候灾害预测', desc: '模拟台风、地震、洪水等自然灾害，提前数月预警', icon: 'ri-thunderstorms-line' },
      ],
      timeline: [
        { year: '2025', event: '元宇宙物理引擎v5.0发布，支持星球级场景' },
        { year: '2027', event: '量子效应模拟功能上线，物理精度达到量子级' },
        { year: '2029', event: '10亿用户并发支持实现，元宇宙大规模商用' },
        { year: '2032', event: '相对论效应模拟完成，虚拟宇宙与现实宇宙物理规律完全一致' },
      ],
      techStack: ['量子物理模拟器', '流体动力学引擎', '光线追踪渲染', '分布式计算网格', 'AI物理预测'],
    },
  },
];

const TIMELINE = [
  { year: '2024', event: 'META66成立，发布元宇宙白皮书', color: '#00FFCC' },
  { year: '2025', event: '6G太赫兹通信首次商业测试成功', color: '#FF6B35' },
  { year: '2026', event: '量子神经网络AI突破10000量子比特', color: '#FFD700' },
  { year: '2027', event: '数字孪生城市项目在大湾区落地', color: '#00FF88' },
  { year: '2028', event: '脑机接口BCI 3.0非侵入式版本发布', color: '#FF6B9D' },
  { year: '2030', event: 'Web5.0协议全球标准化，数字主权时代开启', color: '#A855F7' },
];

// ===== TECH DETAIL PAGE (三级页面) =====
function TechDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const tech = TECH_ITEMS.find(t => t.id === Number(id));
  const [activeTab, setActiveTab] = useState<'overview' | 'applications' | 'timeline' | 'stack'>('overview');
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!tech) return;
    document.title = `${tech.title} | META66核心技术`;
  }, [tech]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    const particles: { x: number; y: number; vx: number; vy: number; r: number; alpha: number }[] = [];
    for (let i = 0; i < 60; i++) {
      particles.push({ x: Math.random() * canvas.width, y: Math.random() * canvas.height, vx: (Math.random() - 0.5) * 0.4, vy: (Math.random() - 0.5) * 0.4, r: Math.random() * 2 + 0.5, alpha: Math.random() * 0.5 + 0.1 });
    }
    let animId: number;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0,255,200,${p.alpha})`;
        ctx.fill();
      });
      animId = requestAnimationFrame(animate);
    };
    animate();
    return () => cancelAnimationFrame(animId);
  }, []);

  if (!tech) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#020810' }}>
        <div className="text-center">
          <p className="text-white/50 text-lg mb-4">技术详情未找到</p>
          <button onClick={() => navigate('/tech')} className="px-6 py-3 rounded-xl text-sm font-bold cursor-pointer" style={{ background: '#00FFCC', color: '#000' }}>返回技术矩阵</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#020810', fontFamily: "'Inter', 'PingFang SC', sans-serif" }}>
      {/* Canvas BG */}
      <canvas ref={canvasRef} className="fixed inset-0 w-full h-full pointer-events-none" style={{ zIndex: 0 }} />

      {/* Header */}
      <div className="sticky top-0 z-50" style={{ background: 'rgba(2,8,16,0.95)', borderBottom: '1px solid rgba(0,255,200,0.1)', backdropFilter: 'blur(20px)' }}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/tech')} className="flex items-center gap-2 text-sm font-medium cursor-pointer whitespace-nowrap transition-all hover:scale-105" style={{ color: tech.color }}>
            <i className="ri-arrow-left-line" />返回技术矩阵
          </button>
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: tech.color }} />
            <span className="text-white/60 text-sm font-medium">{tech.subtitle}</span>
          </div>
          <div className="px-3 py-1 rounded-full text-xs font-bold" style={{ background: `${tech.color}20`, border: `1px solid ${tech.color}40`, color: tech.color }}>{tech.status}</div>
        </div>
      </div>

      {/* Hero */}
      <div className="relative overflow-hidden" style={{ height: '420px', zIndex: 1 }}>
        <img src={tech.image} alt={tech.title} className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(2,8,16,0.3) 0%, rgba(2,8,16,0.7) 60%, rgba(2,8,16,1) 100%)' }} />
        <div className="absolute inset-0 flex items-end pb-12 px-12">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4" style={{ background: `${tech.color}20`, border: `1px solid ${tech.color}40` }}>
              <span className="text-xs font-bold tracking-widest" style={{ color: tech.color }}>META66 · CORE TECHNOLOGY</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-black text-white mb-3" style={{ fontFamily: 'Orbitron, sans-serif' }}>{tech.title}</h1>
            <p className="text-white/60 text-lg max-w-2xl">{tech.desc}</p>
            <div className="flex flex-wrap gap-2 mt-4">
              {tech.tags.map(tag => (
                <span key={tag} className="px-3 py-1 rounded-full text-xs font-bold" style={{ background: `${tech.color}20`, border: `1px solid ${tech.color}40`, color: tech.color }}>{tag}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 py-12">
        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {tech.specs.map(spec => (
            <div key={spec.label} className="p-5 rounded-2xl text-center" style={{ background: `${tech.color}08`, border: `1px solid ${tech.color}20` }}>
              <div className="text-2xl font-black mb-1" style={{ color: tech.color, fontFamily: 'Orbitron, sans-serif' }}>{spec.value}</div>
              <div className="text-white/40 text-xs">{spec.label}</div>
            </div>
          ))}
        </div>

        {/* Progress */}
        <div className="p-6 rounded-2xl mb-12" style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${tech.color}20` }}>
          <div className="flex items-center justify-between mb-3">
            <span className="text-white/70 text-sm font-semibold">研发进度</span>
            <span className="font-black text-lg" style={{ color: tech.color, fontFamily: 'Orbitron, sans-serif' }}>{tech.progress}%</span>
          </div>
          <div className="h-3 rounded-full" style={{ background: 'rgba(255,255,255,0.06)' }}>
            <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${tech.progress}%`, background: `linear-gradient(90deg, ${tech.color}80, ${tech.color})`, boxShadow: `0 0 12px ${tech.color}60` }} />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 flex-wrap">
          {[
            { id: 'overview', label: '技术概述', icon: 'ri-file-text-line' },
            { id: 'applications', label: '应用场景', icon: 'ri-apps-line' },
            { id: 'timeline', label: '发展路线', icon: 'ri-time-line' },
            { id: 'stack', label: '技术栈', icon: 'ri-stack-line' },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap"
              style={{
                background: activeTab === tab.id ? `${tech.color}15` : 'rgba(255,255,255,0.04)',
                border: `1.5px solid ${activeTab === tab.id ? tech.color + '50' : 'rgba(255,255,255,0.08)'}`,
                color: activeTab === tab.id ? tech.color : 'rgba(255,255,255,0.4)',
              }}>
              <i className={tab.icon} />{tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="mb-12">
          {activeTab === 'overview' && (
            <div className="p-8 rounded-2xl" style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${tech.color}15` }}>
              <p className="text-white/70 text-base leading-relaxed" style={{ lineHeight: '2' }}>{tech.deepContent.overview}</p>
            </div>
          )}

          {activeTab === 'applications' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {tech.deepContent.applications.map((app, i) => (
                <div key={i} className="p-6 rounded-2xl transition-all hover:-translate-y-1" style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${tech.color}15` }}>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0" style={{ background: `${tech.color}15` }}>
                      <i className={`${app.icon} text-xl`} style={{ color: tech.color }} />
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-base mb-2">{app.title}</h3>
                      <p className="text-white/50 text-sm leading-relaxed">{app.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'timeline' && (
            <div className="relative">
              <div className="absolute left-6 top-0 bottom-0 w-px" style={{ background: `linear-gradient(to bottom, ${tech.color}80, transparent)` }} />
              <div className="flex flex-col gap-6 pl-16">
                {tech.deepContent.timeline.map((item, i) => (
                  <div key={i} className="relative">
                    <div className="absolute -left-10 top-3 w-4 h-4 rounded-full flex items-center justify-center" style={{ background: `${tech.color}20`, border: `2px solid ${tech.color}60` }}>
                      <div className="w-1.5 h-1.5 rounded-full" style={{ background: tech.color }} />
                    </div>
                    <div className="p-5 rounded-2xl" style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${tech.color}15` }}>
                      <span className="font-black text-sm mr-3" style={{ color: tech.color, fontFamily: 'Orbitron, sans-serif' }}>{item.year}</span>
                      <span className="text-white/60 text-sm">{item.event}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'stack' && (
            <div className="flex flex-wrap gap-3">
              {tech.deepContent.techStack.map((item, i) => (
                <div key={i} className="flex items-center gap-2 px-5 py-3 rounded-xl" style={{ background: `${tech.color}10`, border: `1px solid ${tech.color}30` }}>
                  <div className="w-2 h-2 rounded-full" style={{ background: tech.color }} />
                  <span className="text-white/80 text-sm font-semibold">{item}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Related Techs */}
        <div>
          <h3 className="text-white font-bold text-lg mb-5">相关技术</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {TECH_ITEMS.filter(t => t.id !== tech.id).slice(0, 4).map(t => (
              <button key={t.id} onClick={() => navigate(`/tech/${t.id}`)}
                className="p-4 rounded-2xl text-left transition-all hover:-translate-y-1 cursor-pointer"
                style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${t.color}20` }}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-3" style={{ background: `${t.color}15` }}>
                  <div className="w-2 h-2 rounded-full" style={{ background: t.color }} />
                </div>
                <p className="text-white/80 text-sm font-bold mb-1">{t.title}</p>
                <p className="text-white/30 text-xs">{t.status}</p>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ===== MAIN TECH LIST PAGE =====
function TechListPage() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('all');
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [visibleItems, setVisibleItems] = useState<boolean[]>(new Array(TECH_ITEMS.length).fill(false));

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'META66核心技术矩阵 | 元宇宙·6G·量子计算·脑机接口·XR前沿科技';
    const timers = TECH_ITEMS.map((_, i) => setTimeout(() => {
      setVisibleItems(prev => { const next = [...prev]; next[i] = true; return next; });
    }, i * 100));
    return () => timers.forEach(clearTimeout);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    const particles: { x: number; y: number; vx: number; vy: number; r: number; alpha: number; color: string }[] = [];
    const colors = ['#00FFCC', '#FF6B35', '#FFD700', '#00FF88', '#FF6B9D', '#A855F7'];
    for (let i = 0; i < 80; i++) {
      particles.push({ x: Math.random() * canvas.width, y: Math.random() * canvas.height, vx: (Math.random() - 0.5) * 0.3, vy: (Math.random() - 0.5) * 0.3, r: Math.random() * 1.5 + 0.5, alpha: Math.random() * 0.3 + 0.05, color: colors[Math.floor(Math.random() * colors.length)] });
    }
    let animId: number;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.color.replace(')', `,${p.alpha})`).replace('rgb', 'rgba').replace('#', 'rgba(').replace(')', ',0.15)');
        ctx.fillStyle = `rgba(0,255,200,${p.alpha * 0.6})`;
        ctx.fill();
      });
      animId = requestAnimationFrame(animate);
    };
    animate();
    return () => cancelAnimationFrame(animId);
  }, []);

  const filtered = activeCategory === 'all' ? TECH_ITEMS : TECH_ITEMS.filter(t => t.category === activeCategory);

  return (
    <div className="min-h-screen" style={{ background: '#020810', fontFamily: "'Inter', 'PingFang SC', sans-serif" }}>
      <canvas ref={canvasRef} className="fixed inset-0 w-full h-full pointer-events-none" style={{ zIndex: 0 }} />

      {/* Header */}
      <div className="sticky top-0 z-50" style={{ background: 'rgba(2,8,16,0.95)', borderBottom: '1px solid rgba(0,255,200,0.1)', backdropFilter: 'blur(20px)' }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-sm font-medium cursor-pointer whitespace-nowrap transition-all hover:scale-105" style={{ color: 'rgba(0,255,200,0.7)' }}>
            <i className="ri-arrow-left-line" />返回首页
          </button>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #00FFCC20, #00FFCC10)', border: '1px solid rgba(0,255,200,0.3)' }}>
              <i className="ri-cpu-line text-sm" style={{ color: '#00FFCC' }} />
            </div>
            <span className="font-bold text-white" style={{ fontFamily: 'Orbitron, sans-serif', fontSize: '0.85rem' }}>CORE TECH MATRIX</span>
          </div>
          <div className="text-xs" style={{ color: 'rgba(0,255,200,0.4)' }}>META66 · 8项核心技术</div>
        </div>
      </div>

      {/* Hero Banner */}
      <div className="relative overflow-hidden" style={{ height: '380px', zIndex: 1 }}>
        <img src="https://readdy.ai/api/search-image?query=futuristic%20technology%20laboratory%20quantum%20computing%20holographic%20displays%20neural%20networks%20glowing%20cyan%20neon%20dark%20background%20panoramic%20ultra%20detailed%20cinematic%208k%20sci-fi&width=1920&height=760&seq=techbanner-v3&orientation=landscape" alt="核心技术" className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(2,8,16,0.4) 0%, rgba(2,8,16,0.6) 50%, rgba(2,8,16,1) 100%)' }} />
        <div className="absolute inset-0 flex items-center px-16" style={{ zIndex: 2 }}>
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-5" style={{ background: 'rgba(0,255,200,0.1)', border: '1px solid rgba(0,255,200,0.3)' }}>
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#00FFCC' }} />
              <span className="text-xs font-bold tracking-widest" style={{ color: '#00FFCC' }}>8项核心技术 · 持续突破</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-black text-white mb-4" style={{ fontFamily: 'Orbitron, sans-serif' }}>CORE TECH<br /><span style={{ color: '#00FFCC' }}>MATRIX</span></h1>
            <p className="text-white/50 text-lg max-w-xl">引领元宇宙、6G、量子计算与AI融合的下一代技术体系</p>
          </div>
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-12">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {[
            { label: '核心专利', value: '2,847', icon: 'ri-award-line', color: '#00FFCC' },
            { label: '研发人员', value: '12,000+', icon: 'ri-team-line', color: '#FF6B35' },
            { label: '合作机构', value: '500+', icon: 'ri-building-line', color: '#FFD700' },
            { label: '年研发投入', value: '¥200亿', icon: 'ri-money-cny-circle-line', color: '#00FF88' },
          ].map(stat => (
            <div key={stat.label} className="p-5 rounded-2xl text-center" style={{ background: `${stat.color}08`, border: `1px solid ${stat.color}20` }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mx-auto mb-3" style={{ background: `${stat.color}15` }}>
                <i className={`${stat.icon} text-lg`} style={{ color: stat.color }} />
              </div>
              <div className="text-2xl font-black text-white mb-1" style={{ fontFamily: 'Orbitron, sans-serif' }}>{stat.value}</div>
              <div className="text-white/40 text-xs">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-8">
          {TECH_CATEGORIES.map(cat => (
            <button key={cat.id} onClick={() => setActiveCategory(cat.id)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap"
              style={{
                background: activeCategory === cat.id ? `${cat.color}15` : 'rgba(255,255,255,0.04)',
                border: `1.5px solid ${activeCategory === cat.id ? cat.color + '50' : 'rgba(255,255,255,0.08)'}`,
                color: activeCategory === cat.id ? cat.color : 'rgba(255,255,255,0.4)',
              }}>
              <i className={`${cat.icon} text-sm`} />
              {cat.label}
            </button>
          ))}
        </div>

        {/* Tech Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 mb-16">
          {filtered.map((tech, idx) => (
            <div key={tech.id}
              onClick={() => navigate(`/tech/${tech.id}`)}
              onMouseEnter={() => setHoveredId(tech.id)}
              onMouseLeave={() => setHoveredId(null)}
              className="rounded-2xl overflow-hidden cursor-pointer transition-all duration-300"
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: `1px solid ${hoveredId === tech.id ? tech.color + '60' : tech.color + '15'}`,
                transform: visibleItems[idx] ? (hoveredId === tech.id ? 'translateY(-6px)' : 'translateY(0)') : 'translateY(30px)',
                opacity: visibleItems[idx] ? 1 : 0,
                transition: `transform 0.3s ease, border-color 0.3s ease, opacity 0.5s ease ${idx * 80}ms`,
                boxShadow: hoveredId === tech.id ? `0 8px 30px ${tech.color}20` : 'none',
              }}>
              <div className="relative overflow-hidden" style={{ height: '180px' }}>
                <img src={tech.image} alt={tech.title} className="w-full h-full object-cover object-top transition-transform duration-500" style={{ transform: hoveredId === tech.id ? 'scale(1.05)' : 'scale(1)' }} />
                <div className="absolute inset-0" style={{ background: `linear-gradient(to top, rgba(2,8,16,0.9) 0%, rgba(2,8,16,0.3) 60%, transparent 100%)` }} />
                <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-bold" style={{ background: `${tech.color}20`, border: `1px solid ${tech.color}40`, color: tech.color, backdropFilter: 'blur(8px)' }}>
                  {tech.status}
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-bold text-white text-sm mb-1">{tech.title}</h3>
                <p className="text-white/30 text-xs mb-2">{tech.subtitle}</p>
                <p className="text-white/50 text-xs leading-relaxed mb-3 line-clamp-2">{tech.desc}</p>
                <div className="flex flex-wrap gap-1 mb-3">
                  {tech.tags.map(tag => (
                    <span key={tag} className="px-2 py-0.5 rounded-full text-xs" style={{ background: `${tech.color}10`, color: tech.color, border: `1px solid ${tech.color}20` }}>{tag}</span>
                  ))}
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex-1 h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.06)' }}>
                    <div className="h-full rounded-full" style={{ width: `${tech.progress}%`, background: `linear-gradient(90deg, ${tech.color}60, ${tech.color})` }} />
                  </div>
                  <span className="text-xs font-bold" style={{ color: tech.color }}>{tech.progress}%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-white/30 text-xs">点击查看详情</span>
                  <i className="ri-arrow-right-line text-sm" style={{ color: tech.color }} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Timeline */}
        <div className="mb-16">
          <h2 className="text-3xl font-black text-white mb-10 text-center" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            <span style={{ color: '#00FFCC' }}>TECH</span> TIMELINE
          </h2>
          <div className="relative">
            <div className="absolute left-1/2 top-0 bottom-0 w-px -translate-x-1/2" style={{ background: 'linear-gradient(to bottom, #00FFCC40, transparent)' }} />
            {TIMELINE.map((item, i) => (
              <div key={item.year} className={`flex items-center gap-8 mb-8 ${i % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                <div className={`flex-1 ${i % 2 === 0 ? 'text-right' : 'text-left'}`}>
                  <div className="inline-block p-5 rounded-2xl" style={{ background: `${item.color}08`, border: `1px solid ${item.color}20` }}>
                    <div className="font-black text-2xl mb-1" style={{ color: item.color, fontFamily: 'Orbitron, sans-serif' }}>{item.year}</div>
                    <p className="text-white/60 text-sm">{item.event}</p>
                  </div>
                </div>
                <div className="w-4 h-4 rounded-full border-4 z-10 flex-shrink-0" style={{ background: item.color, borderColor: '#020810', boxShadow: `0 0 12px ${item.color}60` }} />
                <div className="flex-1" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ===== ROUTER WRAPPER =====
export default function TechPage() {
  return (
    <Routes>
      <Route path="/" element={<TechListPage />} />
      <Route path="/:id" element={<TechDetailPage />} />
    </Routes>
  );
}
